import { useInfiniteQuery } from "@tanstack/react-query";
import { toastServerError } from "helpers";
import { ApiError, ApiServiceError } from "libs/api";
import { useMemo } from "react";

import { SUPPORT_QUERY_KEY } from "../../constants/enums";
import {
  InAppConversationMessagesResponse,
  InAppMessage
} from "../../support.types";
import { requestGetConversationMessages } from "./getConversationMessages.request";

interface Params {
  conversationId: string;
  take?: number;
}

const DEFAULT_TAKE = 30;

const useGetConversationMessages = (
  params: Params,
  options?: { enabled?: boolean }
) => {
  const { conversationId, take = DEFAULT_TAKE } = params;
  const enabled = (options?.enabled ?? true) && Boolean(conversationId);

  const query = useInfiniteQuery<
    InAppConversationMessagesResponse,
    ApiServiceError<ApiError>
  >({
    queryKey: [SUPPORT_QUERY_KEY, "messages", conversationId],
    queryFn: async ({ pageParam = 1 }) =>
      await requestGetConversationMessages({
        conversationId,
        take,
        page: pageParam
      }),
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined,
    enabled,
    onError: toastServerError
  });

  const messages = useMemo<InAppMessage[]>(() => {
    const pages = query.data?.pages ?? [];
    const flatDesc = pages.flatMap((p) => p.data);
    return flatDesc.slice().reverse();
  }, [query.data]);

  return {
    messages,
    count: query.data?.pages[0]?.meta.itemCount,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching
  };
};

export default useGetConversationMessages;
